import type { Exercice, Mouvement, Tiers } from '@mairistem/comptabilite';
import dayjs, { Dayjs } from 'dayjs';

type DelaiPaiementDates = Pick<Mouvement, 'dateDepartDelaiGlobalPaiement' | 'dateFinDelaiGlobalPaiement' | 'dureeDelaiGlobalPaiement'>;

const format = (date: Dayjs) : string => date?.format('YYYY-MM-DD');

const addOrSubDays = (
  dates: DelaiPaiementDates,
  date: string,
  name: string,
  isAddition = true,
): DelaiPaiementDates => {
  const dateToModify = dayjs(date);

  return {
    [name]: format(
      isAddition
        ? dateToModify?.add(dates?.dureeDelaiGlobalPaiement, 'day')
        : dateToModify?.subtract(dates?.dureeDelaiGlobalPaiement, 'day'),
    ),
  };
};

/**
 * Au changement de la durée, calculé la nouvelle date de fin.
 */
export const onChangeDuree = (dates: DelaiPaiementDates): DelaiPaiementDates => {
  let changes: DelaiPaiementDates;

  // Si il y'a une date de départ, on change la date de fin
  if (dates.dateDepartDelaiGlobalPaiement) {
    changes = addOrSubDays(dates, dates.dateDepartDelaiGlobalPaiement, 'dateFinDelaiGlobalPaiement');
  } else if (dates.dateFinDelaiGlobalPaiement) { // Si il y'a une date de fin, et pas de date de départ.
    changes = addOrSubDays(dates, dates.dateFinDelaiGlobalPaiement, 'dateDepartDelaiGlobalPaiement', false);
  }

  return {
    ...dates,
    ...changes,
  };
};

/**
 * Ou changement d'une date, calcul de la durée.
 */
export const onChangeDateDepart = (dates: DelaiPaiementDates): DelaiPaiementDates => {
  let changes: DelaiPaiementDates;

  // Si il y'a une durée, on prends la date de début, et on modifie la date de fin.
  if (dates.dateDepartDelaiGlobalPaiement) {
    if (dates.dureeDelaiGlobalPaiement) {
      changes = addOrSubDays(dates, dates.dateDepartDelaiGlobalPaiement, 'dateFinDelaiGlobalPaiement');
    } else if (dates.dateFinDelaiGlobalPaiement) { // Si pas de durée, mais qu'on a les deux dates
      changes = {
        dureeDelaiGlobalPaiement: dayjs(dates.dateFinDelaiGlobalPaiement).diff(dates.dateDepartDelaiGlobalPaiement, 'day'),
      };
    }
  }

  return {
    ...dates,
    ...changes,
  };
};

/**
 * Fonction permettant de mettre une duree par défaut
 */
export const getDureeFromTiersOrExercice = (exercice: Exercice, tiers?: Tiers): Pick<Mouvement, 'dureeDelaiGlobalPaiement'> => {
  // Si on selectionne un tiers
  if (tiers?.delaiPaiement) return { dureeDelaiGlobalPaiement: tiers?.delaiPaiement };

  // Si on selectionne un exercice
  if (exercice.delaiMaxPaiements) return { dureeDelaiGlobalPaiement: exercice.delaiMaxPaiements };

  return {};
};

/**
 * Fonction permettant de mettre les dates et la durée par défaut
 */
export const getDefaultDelaiPaiement = (
  exercice: Exercice,
  tiers?: Tiers,
): DelaiPaiementDates => {
  const duree = getDureeFromTiersOrExercice(exercice, tiers)?.dureeDelaiGlobalPaiement ?? 0;
  const dateDepartDelaiGlobalPaiement = dayjs().format('YYYY-MM-DD');
  const dateFinDelaiGlobalPaiement = format(dayjs().add(duree, 'day'));

  return {
    dateDepartDelaiGlobalPaiement,
    dateFinDelaiGlobalPaiement,
    dureeDelaiGlobalPaiement: duree,
  };
};
